import { ImageBorderPresentation } from '@/engine/UIPresentation/ImageBorderPresentation';
import type {
  IPresentationChangeHandler,
  IUiThreeDPresentation,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext
} from '@/engine/UIPresentation/Interfaces';

import type {
  IImageSliceViewModel,
  IObjectViewModel,
  IViewPortController,
  IViewPortManagerStrategy,
  IRaycasterProvider,
  IMeasurementViewModel,
  ICameraInitializer,
  IRaycastable,
  Intersection,
  ICenterLineViewModel,
  IImagesPresentationFactory,
  Location,
  IViewPortControllerStrategy,
  ICameraController,
  IPickable
} from '../../interfaces';
import { SceneItemKind, ViewPortKind } from '../../interfaces';
import type { ImagesService } from './Images.service';
import type { IImagesPortContext, OnSliceCSChangedListener } from './interfaces';
import { SliceType } from './interfaces';
import * as THREE from 'three';
import { ImagePresentation } from '@/engine/UIPresentation/ImagePresentation';
import type { SlicePositionInfo, SliceOrientationInfo } from './ImageStackController';
import { ViewPortOrientation } from '../../models/ViewPortOrientation';
import { HumanPresentation, SceneCorner } from '@/engine/UIPresentation/HumanPresentation';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IImagePresentationPlaneStrategy } from '@/engine/UIPresentation/PresentationVisibilityStrategies';
import { Image2dPresentationPlaneStrategy } from '@/engine/UIPresentation/PresentationVisibilityStrategies';
import { MprAxisPicker2d } from './MprAxisPicker2d';
import type { IPointerController } from '@/components/viewPort/PointerController';
import { PointerHandlerAdjustCamera } from '@/components/viewPort/PointerHandlerAdjustCamera';
import { PointerHandlerAdjustContrast } from './PointerHandlerAdjustContrast';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import { safeResource } from '@/engine/Utilities/IDisposable';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import { PointerHandlerSynchronizeImages } from './PointerHandlerSynchronizeImages';
import { PointerHandlerScrollImages } from './PointerHandlerScrollImages';
import { PointerHandlerGhostCursor } from './PointerHandlerGhostCursor';
import type { ViewsService } from '../../Views.service';
import { ImagesCameraInitializer } from './ImagesCameraInitializer';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { ImagesAndAreaDependencyController } from '../../../Controllers/ImagesAndAreaDependency.controller';
import { getPlaneLineIntersectionPoint } from '@/engine/utils/geometryHelper';
import type { ViewController } from '../../View.controller';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ICommandProcessor } from '../core/interfaces/Command';
import type { IKeyboardController } from '@/components/viewPort/KeyboardController.ts';
import { KeyboardImagesHandler } from '@/engine/app/modules/images/KeyboardImagesHandler.ts';
import type { IImageScrollStrategiesFactory } from '@/engine/app/modules/images/ImageScrollStrategies.factory.ts';

export class ViewPortImagesStrategy
  implements IViewPortManagerStrategy, IViewPortControllerStrategy, IImagesPortContext, IRaycastable
{
  private readonly _viewportController: IViewPortController;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _imageScrollStrategiesFactory: IImageScrollStrategiesFactory;

  private readonly _mainSliceType: SliceType;

  private _mainSliceContext?: SliceOrientationInfo;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _disposables: DisposablesBasket = new DisposablesBasket();

  private _raycasterProvider?: IRaycasterProvider;

  private readonly _onSliceCSChangedListeners: CallbackManager<OnSliceCSChangedListener> =
    new CallbackManager<OnSliceCSChangedListener>();

  private readonly _defaultCameraInitializer: ImagesCameraInitializer;

  private _areImagesReady = false;

  private _mainSlicePlane?: {
    origin: THREE.Vector3;
    normal: THREE.Vector3;
  };

  private _presentationFactory: IImagesPresentationFactory;

  public constructor(
    viewportController: IViewPortController,
    planningAlgorithm: PlanningAlgorithm,
    imagesService: ImagesService,
    viewsService: ViewsService,
    viewController: ViewController,
    mainSliceType: SliceType,
    presentationFactory: IImagesPresentationFactory,
    loggerService: ObservabilityLoggerService,
    commandProcessor: ICommandProcessor,
    imageScrollStrategiesFactory: IImageScrollStrategiesFactory
  ) {
    this._viewportController = viewportController;
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._imageScrollStrategiesFactory = imageScrollStrategiesFactory;
    this._mainSliceType = mainSliceType;
    this._presentationFactory = presentationFactory;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;

    this._defaultCameraInitializer = new ImagesCameraInitializer(
      this._viewportController,
      viewsService,
      this._planningAlgorithm
    );
    this._disposables.add(
      this._imagesService.subscribeToSliceOrientationChange(
        this._mainSliceType,
        this._handleSliceOrientationChange.bind(this)
      )
    );
    this._disposables.add(
      this._imagesService.subscribeToSlicePositionChange(
        this._mainSliceType,
        this._handleSlicePositionChange.bind(this)
      )
    );
    this._disposables.add(
      this._imagesService.subscribeToSynchronizationPointChanged(
        this._handleImagesSynchronizationPointChanged.bind(this)
      )
    );
    this._disposables.add(
      this._imagesService.subscribeToRoiPointRequested(this._handleImagesRoiPointRequested.bind(this))
    );
    this._disposables.add(
      this._imagesService.subscribeToReadyStateChange(this._handleImagesReadyStateChange.bind(this))
    );
  }

  public getViewPortControllerStrategy(): IViewPortControllerStrategy {
    return this;
  }

  // IViewPortControllerStrategy
  public setViewRoi(location: Location, zoomFactor: number): void {
    // do not set camera orientation via CameraController.setCameraOrientation
    // for an images ViewPort as it is being managed separately
    // it must be always in sync with image plane, so set Images MPR instead:
    this._imagesService.syncSliceWithPlane(this._mainSliceType, location.ptO, location.dirZ, location.dirY);
    this._viewportController
      .getCameraController()
      .fitCameraToPoints([location.minCorner, location.maxCorner], { fittingFactor: zoomFactor });
  }

  // IViewPortControllerStrategy
  public synchronizeCamera(anotherCamera: ICameraController): void {
    if (!this._mainSliceContext) {
      throw new Error('Logic Error');
    }
    const normal = new THREE.Vector3(0, 0, 1).transformDirection(this._mainSliceContext.volumeCS);
    const dir = anotherCamera.getCameraDirection();
    // TODO: [chebotarev] explicit CORONAl image's specific behavior is bad
    if (this._mainSliceType == SliceType.CORONAL) {
      dir.negate();
    }
    if (normal.distanceToSquared(dir) > 1e-8) {
      // 0 - parallel codirectional, 4 - parallel opposite directional
      // synchronize plane only if camera is not perpendicular to the plane:
      //   speed up Zoom, Pan and In-plane-rotation
      const top = anotherCamera.getCameraTop();
      const center = this._imagesService.getSlicesIntersectionPoint();
      this._imagesService.syncSliceWithPlane(this._mainSliceType, center, dir, top, undefined, true);
    }
    this._viewportController.getCameraController().synchronizeWith(anotherCamera);
  }

  // IViewPortControllerStrategy
  public setNoRotateCamera(noRotate: boolean): void {
    if (!noRotate) {
      throw new Error('Logic Error');
    }
  }

  public setRaycasterProvider(provider: IRaycasterProvider): void {
    this._raycasterProvider = provider;
    this._presentationFactory.setRaycasterProvider(provider);
  }

  public getDefaultPickables(): Array<IPickable> | undefined {
    return [
      new MprAxisPicker2d(
        this._imagesService,
        this._viewportController.getCameraController(),
        this._mainSliceType,
        this._loggerService
      )
    ];
  }

  public createDefaultCameraInitializer(): ICameraInitializer {
    return this._defaultCameraInitializer;
  }

  public createKeyboardHandlers(keyboardController: IKeyboardController): void {
    keyboardController.addKeyboardHandler(new KeyboardImagesHandler(this._imagesService, this._mainSliceType));
  }

  public createPointerHandlers(pointerController: IPointerController): void {
    if (!this._raycasterProvider) {
      throw new EvalError('Raycaster provider must be set at this point');
    }

    pointerController.pushPointerHandler(
      new PointerHandlerGhostCursor(
        this._viewportController.getCameraController(),
        this._raycasterProvider.getRaycaster(),
        this._viewsService
      ),
      1
    );

    pointerController.pushPointerHandler(
      new PointerHandlerAdjustCamera(this._viewportController, this._raycasterProvider.getRaycaster(), true, true)
    );

    pointerController.pushPointerHandler(new PointerHandlerAdjustContrast(this._imagesService));

    pointerController.pushPointerHandler(
      new PointerHandlerSynchronizeImages(
        this._imagesService,
        this._raycasterProvider.getRaycaster(),
        this._viewController,
        this._mainSliceType
      )
    );

    pointerController.pushPointerHandler(
      new PointerHandlerScrollImages(this._imagesService, this._imageScrollStrategiesFactory, this._mainSliceType)
    );
  }

  public createMeasurementLabel(): IUiTwoDPresentationContext | undefined {
    return undefined;
  }

  public createCenterLinePresentation(
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    return this._presentationFactory.createCenterLinePresentation(this, viewModel, changeHandler);
  }

  public createImagePresentation(
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    const planeStrategy: IImagePresentationPlaneStrategy = new Image2dPresentationPlaneStrategy();
    if (imageModel.descriptor === this._mainSliceType) {
      const presentation = new ImagePresentation(this._imagesService, imageModel, changeHandler, planeStrategy);
      this._defaultCameraInitializer.setImagePresentation(presentation);
      this._defaultCameraInitializer.release();
      return {
        presentation: presentation,
        raycastable: this
      };
    } else {
      return {
        presentation: new ImageBorderPresentation(this._imagesService, imageModel, changeHandler, this)
      };
    }
  }

  public createThreeDObjectPresentation(
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._presentationFactory.createThreeDObjectPresentation(this, viewModel, changeHandler);
  }

  public createThreeDMeasurementPresentation(
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._presentationFactory.createThreeDMeasurementPresentation(this, viewModel, changeHandler);
  }

  public createTwoDObjectPresentation(viewModel: IObjectViewModel): IUiTwoDPresentationContext | undefined {
    return this._presentationFactory.createTwoDObjectPresentation(this, viewModel);
  }

  public createMeasurementTwoDPresentation(viewModel: IMeasurementViewModel): IUiTwoDPresentationContext | undefined {
    return this._presentationFactory.createTwoDMeasurementPresentation(this, viewModel);
  }

  public rayCast(origin: THREE.Vector3, direction: THREE.Vector3 /*, doNotOptimize?: boolean*/): Array<Intersection> {
    if (this._mainSlicePlane) {
      const intersectionPt = getPlaneLineIntersectionPoint(
        this._mainSlicePlane.origin,
        this._mainSlicePlane.normal,
        origin,
        direction
      );
      if (intersectionPt) {
        return [
          {
            point: intersectionPt,
            distance: intersectionPt.distanceTo(origin),
            descriptor: this._mainSliceType
          }
        ];
      }
    }
    return [];
  }

  public createDependencyController(viewModel: IMeasurementViewModel): IDisposable | undefined {
    const type = viewModel.kind;

    if (type === SceneItemKind.AreaMeasurement || type === SceneItemKind.DiameterMeasurement) {
      if (viewModel.syncSliceDescriptor == this._mainSliceType) {
        return new ImagesAndAreaDependencyController(
          viewModel,
          this._imagesService,
          this._planningAlgorithm,
          this,
          this._mainSliceType,
          this._commandProcessor
        );
      }
    }
    return;
  }

  public createViewHelpersPresentations(changeHandler: IPresentationChangeHandler): Array<IUiThreeDPresentation> {
    const presentations: Array<IUiThreeDPresentation> = [];
    presentations.push(
      new HumanPresentation(
        SceneCorner.RightBottom,
        27,
        27,
        new THREE.Vector2(38, 23),
        new THREE.Vector2(53, 33),
        changeHandler,
        this._viewportController,
        new THREE.Color(0x4fc8ff),
        0.7
      )
    );
    return presentations;
  }

  public resetView(): void {
    this._viewController.resetView(ViewPortKind.ImageSlice);
  }

  public dispose(): void {
    this._disposables.dispose();
  }

  public addOnSliceCSChangedListener(
    callback: OnSliceCSChangedListener,
    immediateNotify?: boolean
  ): CallbackSubscription {
    return safeResource(this._onSliceCSChangedListeners.subscribe(callback), () => {
      if (immediateNotify === true) {
        const positionInfo = this._imagesService.getSlicePositionInfo(this._mainSliceType);
        if (positionInfo) {
          callback(this._getSliceCS(positionInfo), positionInfo);
        }
      }
    });
  }

  private _getOrientation(sliceContext: SliceOrientationInfo): ViewPortOrientation {
    const topVector = new THREE.Vector3(0, 1, 0).transformDirection(sliceContext.volumeCS);
    const directionVector = new THREE.Vector3().crossVectors(
      new THREE.Vector3(1, 0, 0).transformDirection(sliceContext.volumeCS),
      new THREE.Vector3(0, 1, 0).transformDirection(sliceContext.volumeCS)
    );

    return new ViewPortOrientation(topVector, directionVector);
  }

  private _handleSliceOrientationChange(orientationInfo: SliceOrientationInfo): void {
    this._mainSliceContext = orientationInfo;
    if (this._imagesService.isReady()) {
      if (this._mainSliceContext.deltaTransformation) {
        this._viewportController.getCameraController().transformCamera(this._mainSliceContext.deltaTransformation);
      } else {
        // otherwise let's set orientation and re-fit camera
        this._viewportController.setupCamera(this._getOrientation(this._mainSliceContext));
        this._defaultCameraInitializer.initialize();
      }
    }
  }

  private _getSliceCS(positionInfo: SlicePositionInfo): THREE.Matrix4 {
    if (!this._mainSliceContext) {
      throw new Error('mainSliceContext expected to be initialized so far');
    }
    const matrix = positionInfo.sliceCS.clone();
    const pos = new THREE.Vector3(
      this._mainSliceContext.volumeDimensions.x / 2,
      this._mainSliceContext.volumeDimensions.y / 2,
      0
    ).applyMatrix4(matrix);
    matrix.setPosition(pos);
    return matrix;
  }

  private _handleSlicePositionChange(positionInfo: SlicePositionInfo): void {
    this._mainSlicePlane = {
      origin: new THREE.Vector3(0, 0, 0).applyMatrix4(positionInfo.sliceCS),
      normal: new THREE.Vector3(0, 0, 1).transformDirection(positionInfo.sliceCS)
    };
    this._onSliceCSChangedListeners.invoke(this._getSliceCS(positionInfo), positionInfo);
  }

  public get sliceDistance(): number {
    if (!this._mainSliceContext) {
      return 0.5; // TODO: [chebotarev] stubbed with 0.5 to avoid crash. Maybe it should be undefined here?
    } else {
      return this._mainSliceContext.voxelDimensions['z'];
    }
  }

  private _handleImagesSynchronizationPointChanged(synchronizationPoint: THREE.Vector3): void {
    this._viewportController.getCameraController().ensurePointIsVisible(synchronizationPoint, 0.1);
  }

  private _handleImagesRoiPointRequested(roiPoint: THREE.Vector3, sliceType?: SliceType): void {
    const center = this._imagesService.getSlicesIntersectionPoint();
    this._viewportController.getCameraController().centrifyCamera(center, sliceType === this._mainSliceType);
    this._viewportController.getCameraController().ensurePointIsVisible(roiPoint, 0.1);
  }

  private _handleImagesReadyStateChange(areImagesReady: boolean): void {
    this._areImagesReady = areImagesReady;
    if (this._mainSlicePlane && !areImagesReady) {
      this._mainSlicePlane = undefined;
    }
    if (!this._mainSliceContext?.deltaTransformation) {
      this._defaultCameraInitializer.setAreImagesReady(this._areImagesReady);
      if (areImagesReady) {
        if (!this._mainSliceContext) {
          throw new Error('mainSliceContext expected to be initialized so far');
        }
        this._viewportController.setupCamera(this._getOrientation(this._mainSliceContext));
        this._defaultCameraInitializer.initialize();
      }
    }
  }
}
